import { useState, useEffect, useRef, useCallback } from 'react'
import { withMountBase } from '../lib/mountBase'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface SessionState {
  session_id: string
  model: string
  cwd: string
  tools: string[]
  permissionMode: string
  claude_code_version: string
  slash_commands: string[]
  skills: string[]
  agents: string[]
  /** Frontmatter-derived per-command metadata (description, argument-hint). */
  command_meta?: Record<string, { description?: string; argument_hint?: string }>
  /** Dynamic model catalog relayed from the CLI's list_models. */
  models?: { value: string; resolved_model: string; display_name: string; description: string }[]
  total_cost_usd: number
  num_turns: number
}

export interface ContentBlock {
  type: string
  text?: string
  id?: string
  name?: string
  input?: Record<string, unknown>
  tool_use_id?: string
  content?: string | ContentBlock[]
  is_error?: boolean
  thinking?: string
}

export interface AssistantMessagePayload {
  id: string
  type: string
  role: string
  model: string
  content: ContentBlock[]
  stop_reason: string | null
  usage: {
    input_tokens: number
    output_tokens: number
    cache_creation_input_tokens: number
    cache_read_input_tokens: number
  }
}

export interface PermissionRequest {
  request_id: string
  tool_name: string
  input: Record<string, unknown>
  permission_suggestions?: unknown[]
  description?: string
  tool_use_id: string
  timestamp: number
}

export interface QuestionOption {
  label: string
  description?: string
}

export interface QuestionItem {
  header?: string
  question: string
  options: QuestionOption[]
}

export interface QuestionRequest {
  request_id: string
  tool_name: string
  input: {
    questions?: QuestionItem[]
    question?: string
    [key: string]: unknown
  }
  description?: string
  tool_use_id?: string
  timestamp: number
}

export interface ResultData {
  subtype: string
  is_error: boolean
  result?: string
  duration_ms: number
  num_turns: number
  total_cost_usd: number
}

export interface ToolProgress {
  toolName: string
  elapsed: number
}

export interface ImageAttachment {
  media_type: string
  data: string
}

export type ChatMessage =
  | {
      type: 'user_message'
      content: string
      images?: ImageAttachment[]
      timestamp: number
      /**
       * Explicit false marks a backfilled message that was resolved entirely
       * inside the CLI (a local slash command like /copy, /cost — a
       * `<local-command-stdout>` echo followed it in the transcript, no
       * assistant turn). Such a message can never be "awaiting a reply", no
       * matter how recent — unlike a real prompt-expanding command, which
       * DOES dispatch to the model. Undefined (the common case) means the
       * normal recency-based heuristic applies. See transcript.js
       * `localCommandHasNoReply`.
       */
      awaitingReply?: boolean
      /**
       * Backfilled user-ROLE lines the user did not type: harness/system
       * notifications ('system-note') and compaction summaries
       * ('compaction'). Rendered as a collapsed system card, not as the
       * user's own bubble. Undefined = a real typed message.
       */
      kind?: 'system-note' | 'compaction'
    }
  | { type: 'assistant'; message: AssistantMessagePayload; parent_tool_use_id: string | null; timestamp: number }
  | { type: 'result'; data: ResultData }
  | { type: 'permission_request'; request: PermissionRequest }
  | { type: 'system_event'; event: unknown; timestamp: number }
  | { type: 'tool_use_summary'; summary: string; tool_use_ids: string[] }
  | { type: 'screenshot_result'; tool_use_id: string; filename: string; media_type: string; data: string }

export interface LoginState {
  status: 'idle' | 'awaiting_url' | 'awaiting_code' | 'submitting' | 'complete' | 'error'
  url?: string
  error?: string
}

export interface ConversationSummary {
  id: string
  title: string | null
  archived: boolean
  active: boolean
  created_at: number | null
  updated_at: number
  size: number
}

export interface UseClaudeSessionReturn {
  connected: boolean
  cliConnected: boolean
  messages: ChatMessage[]
  sessionState: SessionState | null
  pendingPermissions: PermissionRequest[]
  pendingQuestions: QuestionRequest[]
  streamingText: string
  streamingThinking: string
  isStreaming: boolean
  isCompacting: boolean
  activeTools: Map<string, ToolProgress>
  loginState: LoginState
  /** Bridge session phase (ground truth), null until the first session_phase frame. */
  phase: string | null
  conversations: ConversationSummary[]
  listConversations: () => void
  resumeConversation: (id: string) => void
  sendMessage: (text: string, images?: ImageAttachment[]) => void
  respondToPermission: (requestId: string, behavior: 'allow' | 'deny') => void
  respondToQuestion: (requestId: string, answers: Record<string, string>, behavior?: 'allow' | 'deny') => void
  interrupt: () => void
  setModel: (model: string) => void
  setAgent: (kind: string) => void
  setPermissionMode: (mode: string) => void
  startLogin: () => void
  submitLoginCode: (code: string) => void
  logout: () => void
  newSession: () => void
}

/**
 * Merge assistant message content blocks: if the new version drops text/thinking
 * blocks that were in the old version, preserve them. This prevents Claude's
 * explanation text from disappearing when tool_use blocks are added.
 */
function mergeAssistantMessage(oldMsg: ChatMessage, newMsg: Record<string, unknown>): ChatMessage {
  const oldContent = (oldMsg as { message?: { content?: ContentBlock[] } }).message?.content || []
  const newContent = ((newMsg as { message?: { content?: ContentBlock[] } }).message?.content) || []
  let merged = [...newContent]
  let changed = false

  // Preserve text/thinking blocks if the new version drops them
  const newHasText = newContent.some(b => b.type === 'text' || b.type === 'thinking')
  const oldTextBlocks = oldContent.filter(b => b.type === 'text' || b.type === 'thinking')
  if (oldTextBlocks.length > 0 && !newHasText) {
    merged = [...oldTextBlocks, ...merged]
    changed = true
  }

  // Preserve Agent/Task tool_use blocks if the new version drops them.
  // Without these, subagent threads become "orphaned" and render at the bottom
  // instead of inline after their parent message.
  const newAgentIds = new Set(merged.filter(b => b.type === 'tool_use' && (b.name === 'Agent' || b.name === 'Task')).map(b => b.id))
  const missingAgents = oldContent.filter(b => b.type === 'tool_use' && (b.name === 'Agent' || b.name === 'Task') && b.id && !newAgentIds.has(b.id))
  if (missingAgents.length > 0) {
    merged = [...merged, ...missingAgents]
    changed = true
  }

  // Preserve tool_result blocks that were dropped (final assistant messages
  // lose them because tool_result is a user message in the API)
  const newResultIds = new Set(merged.filter(b => b.type === 'tool_result').map(b => b.tool_use_id))
  const missingResults = oldContent.filter(b => b.type === 'tool_result' && !newResultIds.has(b.tool_use_id))
  if (missingResults.length > 0) {
    for (const result of missingResults) {
      const useIdx = merged.findIndex(b => b.type === 'tool_use' && b.id === result.tool_use_id)
      if (useIdx >= 0) {
        merged.splice(useIdx + 1, 0, result)
      }
    }
    changed = true
  }

  if (changed) {
    return {
      ...newMsg,
      message: { ...(newMsg as { message: Record<string, unknown> }).message, content: merged },
    } as unknown as ChatMessage
  }
  return newMsg as unknown as ChatMessage
}

// ─── Hook ───────────────────────────────────────────────────────────────────

// Session cache keys
const cacheKey = (sid: string, suffix: string) => `claude-cache-${sid}-${suffix}`

function loadCached<T>(sid: string, suffix: string): T | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(sid, suffix))
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function saveCache(sid: string, suffix: string, data: unknown) {
  try {
    sessionStorage.setItem(cacheKey(sid, suffix), JSON.stringify(data))
  } catch { /* quota exceeded — ignore */ }
}

export function useClaudeSession(appName: string, sessionId: string, agent?: string): UseClaudeSessionReturn {
  const [connected, setConnected] = useState(false)
  const [cliConnected, setCliConnected] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadCached(sessionId, 'msgs') || [])
  const [sessionState, setSessionState] = useState<SessionState | null>(() => loadCached(sessionId, 'state'))
  const [pendingPermissions, setPendingPermissions] = useState<PermissionRequest[]>([])
  const [pendingQuestions, setPendingQuestions] = useState<QuestionRequest[]>(() => loadCached(sessionId, 'questions') || [])
  const [loginState, setLoginState] = useState<LoginState>({ status: 'idle' })
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [phase, setPhase] = useState<string | null>(null)

  // Streaming state — use refs for fast WebSocket accumulation, sync to state via rAF
  const [streamingText, setStreamingText] = useState('')
  const [streamingThinking, setStreamingThinking] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const streamingTextRef = useRef('')
  const streamingThinkingRef = useRef('')
  const isStreamingRef = useRef(false)
  const streamRafRef = useRef<number>(0)

  // Track highest seq number received for smart replay on reconnect
  const lastSeqRef = useRef(0)

  // Sync streaming refs → state at animation frame cadence
  const syncStreamingState = useCallback(() => {
    if (streamRafRef.current) return // already scheduled
    streamRafRef.current = requestAnimationFrame(() => {
      streamRafRef.current = 0
      setStreamingText(streamingTextRef.current)
      setStreamingThinking(streamingThinkingRef.current)
      setIsStreaming(isStreamingRef.current)
    })
  }, [])

  const clearStreamingState = useCallback(() => {
    if (streamRafRef.current) {
      cancelAnimationFrame(streamRafRef.current)
      streamRafRef.current = 0
    }
    streamingTextRef.current = ''
    streamingThinkingRef.current = ''
    isStreamingRef.current = false
    setStreamingText('')
    setStreamingThinking('')
    setIsStreaming(false)
  }, [])

  // Tool progress state
  const [activeTools, setActiveTools] = useState<Map<string, ToolProgress>>(new Map())

  // Compacting state
  const [isCompacting, setIsCompacting] = useState(false)

  // Shared reset for state that describes the OLD session/turn and must not
  // leak into the new one: pending permission/question prompts, in-flight
  // tool progress, the compacting flag, and streaming buffers. Also drops
  // last_seq — the bridge's per-session sequence counter restarts at 1, so
  // carrying over the old value can make the new session's subscribe look
  // "already caught up" and never receive its message_history at all. Used
  // by newSession, resumeConversation, and the sessionId-change effect below
  // (which layers its own extras — cached history, phase, cliConnected — on
  // top).
  const resetTransientSessionState = useCallback(() => {
    setPendingPermissions([])
    setPendingQuestions([])
    setActiveTools(new Map())
    setIsCompacting(false)
    clearStreamingState()
    lastSeqRef.current = 0
  }, [clearStreamingState])

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectDelayRef = useRef(1000)
  const mountedRef = useRef(true)

  // Store message handler in ref to avoid recreating WebSocket on handler changes
  const handleMessageRef = useRef<(msg: Record<string, unknown>) => void>(() => {})

  const connect = useCallback(() => {
    if (!appName || !sessionId) {
      console.warn('[claude-session] connect() skipped: appName=%s sessionId=%s', appName, sessionId)
      return
    }

    // Close existing connection to prevent duplicates (React strict mode, re-renders)
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    if (wsRef.current) {
      wsRef.current.onclose = null   // Prevent stale reconnect trigger
      wsRef.current.onmessage = null // Prevent stale messages after remount
      wsRef.current.close()
      wsRef.current = null
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsPath = withMountBase(
      `/workspace/app/${encodeURIComponent(appName)}/claude/${sessionId}`,
      window.location.pathname
    )
    const wsUrl = `${protocol}//${window.location.host}${wsPath}`

    console.log('[claude-session] Connecting to:', wsUrl)
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      if (!mountedRef.current) return
      console.log('[claude-session] WebSocket opened, last_seq=%d', lastSeqRef.current)
      setConnected(true)
      reconnectDelayRef.current = 1000
      // The bridge always replays its complete, authoritative pending-
      // permission / pending-question set unprompted right after a fresh
      // connection (see server.js). Reset local state first so a stale
      // sessionStorage-cached card — already resolved or expired
      // server-side, e.g. left over from a previous browser tab or a
      // different work area sharing this hook instance — can't linger
      // forever with nothing left to ever clear it, and a plain reconnect
      // can't duplicate an already-known entry.
      setPendingPermissions([])
      setPendingQuestions([])
      // Subscribe with last known seq for smart replay
      ws.send(JSON.stringify({ type: 'session_subscribe', last_seq: lastSeqRef.current, agent: agentRef.current }))
    }

    ws.onmessage = (event) => {
      if (!mountedRef.current) return
      try {
        handleMessageRef.current(JSON.parse(event.data))
      } catch {
        // Ignore parse errors
      }
    }

    ws.onclose = (event) => {
      if (!mountedRef.current) return
      console.warn('[claude-session] WebSocket closed: code=%d reason=%s delay=%dms', event.code, event.reason, reconnectDelayRef.current)
      setConnected(false)
      setCliConnected(false)
      wsRef.current = null

      // Reconnect with exponential backoff
      const delay = reconnectDelayRef.current
      reconnectDelayRef.current = Math.min(delay * 2, 30000)
      reconnectTimeoutRef.current = setTimeout(() => {
        if (mountedRef.current) connect()
      }, delay)
    }

    ws.onerror = (event) => {
      console.error('[claude-session] WebSocket error:', event)
      // onclose will fire after this
    }
  }, [appName, sessionId])

  // Message handler — update ref on each render to capture latest state setters
  handleMessageRef.current = (msg: Record<string, unknown>) => {
    // Track highest seq for smart replay on reconnect
    const seq = msg.seq as number | undefined
    if (typeof seq === 'number' && seq > lastSeqRef.current) {
      lastSeqRef.current = seq
    }
    const type = msg.type as string

    switch (type) {
      case 'session_init': {
        const incoming = msg.session as SessionState
        setSessionState(prev => {
          if (!prev) return incoming
          // Preserve array references when contents haven't changed to avoid
          // unnecessary downstream recomputations (slash command dropdown, etc.)
          const sameSlash = prev.slash_commands.length === incoming.slash_commands.length
            && prev.slash_commands.every((c, i) => c === incoming.slash_commands[i])
          const sameSkills = prev.skills.length === incoming.skills.length
            && prev.skills.every((s, i) => s === incoming.skills[i])
          const sameAgents = prev.agents.length === incoming.agents.length
            && prev.agents.every((a, i) => a === incoming.agents[i])
          // Same treatment for command_meta/models: they feed ClaudePanel's
          // allCommands/activeCommandHint/MODELS memos, so an unchanged
          // catalog on a session_init rebroadcast (e.g. reconnect) must not
          // bust a reference identity those memos depend on.
          const prevMeta = prev.command_meta
          const nextMeta = incoming.command_meta
          const sameCommandMeta = prevMeta === nextMeta || (
            !!prevMeta && !!nextMeta
            && Object.keys(prevMeta).length === Object.keys(nextMeta).length
            && Object.keys(prevMeta).every(k => nextMeta[k]
              && prevMeta[k].description === nextMeta[k].description
              && prevMeta[k].argument_hint === nextMeta[k].argument_hint)
          )
          const prevModels = prev.models
          const nextModels = incoming.models
          const sameModels = prevModels === nextModels || (
            !!prevModels && !!nextModels
            && prevModels.length === nextModels.length
            && prevModels.every((m, i) => m.value === nextModels[i].value
              && m.resolved_model === nextModels[i].resolved_model
              && m.display_name === nextModels[i].display_name
              && m.description === nextModels[i].description)
          )
          return {
            ...incoming,
            slash_commands: sameSlash ? prev.slash_commands : incoming.slash_commands,
            skills: sameSkills ? prev.skills : incoming.skills,
            agents: sameAgents ? prev.agents : incoming.agents,
            command_meta: sameCommandMeta ? prev.command_meta : incoming.command_meta,
            models: sameModels ? prev.models : incoming.models,
          }
        })
        setCliConnected(true)
        break
      }

      case 'message_history': {
        // Always trust the server's history as authoritative — including empty.
        // This clears stale sessionStorage cache after session reset.
        const incoming = msg.messages as ChatMessage[]
        setMessages(incoming)
        break
      }

      case 'cli_connected':
        setCliConnected(true)
        break

      case 'cli_disconnected':
        setCliConnected(false)
        clearStreamingState()
        setActiveTools(new Map())
        setIsCompacting(false)
        break

      case 'user_message':
        setMessages(prev => [...prev, msg as unknown as ChatMessage])
        break

      case 'assistant': {
        // With --include-partial-messages, the CLI sends intermediate assistant messages
        // with stop_reason=null while generating, then a final one with stop_reason set.
        const aMsg = (msg as { message?: { stop_reason?: string | null } }).message
        const isFinal = aMsg?.stop_reason != null
        if (isFinal) {
          clearStreamingState()
        } else {
          // Clear accumulated streaming text — the stored message already has it.
          // This prevents the streaming bubble from duplicating content.
          // New stream_event deltas will re-accumulate from the next delta.
          streamingTextRef.current = ''
          streamingThinkingRef.current = ''
          isStreamingRef.current = true
          syncStreamingState()
        }
        // Deduplicate by message ID — replace if same ID already exists, else append
        // Merge content: preserve text/thinking blocks if the new version drops them
        const aMsgId = (msg as { message?: { id?: string } }).message?.id
        setMessages(prev => {
          if (aMsgId) {
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].type === 'assistant' && (prev[i] as { message?: { id?: string } }).message?.id === aMsgId) {
                const updated = [...prev]
                updated[i] = mergeAssistantMessage(prev[i], msg)
                return updated
              }
            }
          }
          return [...prev, msg as unknown as ChatMessage]
        })
        break
      }

      case 'assistant_replace': {
        // CLI resent an assistant message with the same ID (content updated) — replace in-place.
        // With --include-partial-messages, these arrive rapidly as content streams in.
        const replaceId = (msg as { message_id?: string }).message_id
          || (msg as { message?: { id?: string } }).message?.id
        const rMsg = (msg as { message?: { stop_reason?: string | null } }).message
        const rIsFinal = rMsg?.stop_reason != null
        if (rIsFinal) {
          clearStreamingState()
        } else {
          // Clear accumulated streaming text — the stored message already has it.
          streamingTextRef.current = ''
          streamingThinkingRef.current = ''
          syncStreamingState()
        }
        // Store with type 'assistant' so MessageBubble renders it correctly
        // Merge content: preserve text/thinking blocks if the new version drops them
        const assistantRaw = { ...msg, type: 'assistant' }
        setMessages(prev => {
          let idx = -1
          for (let i = prev.length - 1; i >= 0; i--) {
            const m = prev[i]
            if (m.type === 'assistant' && (m as { message?: { id?: string } }).message?.id === replaceId) {
              idx = i
              break
            }
          }
          if (idx >= 0) {
            const updated = [...prev]
            updated[idx] = mergeAssistantMessage(prev[idx], assistantRaw)
            return updated
          }
          return [...prev, assistantRaw as unknown as ChatMessage]
        })
        break
      }

      case 'result':
        // Turn complete — clear all transient state
        clearStreamingState()
        setActiveTools(new Map())
        setIsCompacting(false)
        setMessages(prev => [...prev, msg as unknown as ChatMessage])
        break

      case 'permission_request':
        clearStreamingState()
        setPendingPermissions(prev => [...prev, (msg as { request: PermissionRequest }).request])
        break

      case 'question_request':
        clearStreamingState()
        setPendingQuestions(prev => [...prev, (msg as { request: QuestionRequest }).request])
        break

      case 'permission_cancelled': {
        const cancelledId = msg.request_id as string
        setPendingPermissions(prev => prev.filter(p => p.request_id !== cancelledId))
        setPendingQuestions(prev => prev.filter(q => q.request_id !== cancelledId))
        break
      }

      // ── Streaming events ─────────────────────────────────────────────
      case 'stream_event': {
        const event = msg.event as Record<string, unknown> | undefined
        if (!event) break
        const eventType = event.type as string

        if (eventType === 'content_block_start') {
          isStreamingRef.current = true
          syncStreamingState()
        }

        if (eventType === 'content_block_delta') {
          const delta = event.delta as Record<string, unknown> | undefined
          if (!delta) break
          if (delta.type === 'text_delta' && typeof delta.text === 'string') {
            streamingTextRef.current += delta.text as string
            isStreamingRef.current = true
            syncStreamingState()
          }
          if (delta.type === 'thinking_delta' && typeof delta.thinking === 'string') {
            streamingThinkingRef.current += delta.thinking as string
            isStreamingRef.current = true
            syncStreamingState()
          }
        }

        // Don't clear streaming on content_block_stop — more blocks may follow.
        // Streaming clears when the final `assistant` or `assistant_replace` message arrives.
        break
      }

      // ── Tool progress ────────────────────────────────────────────────
      case 'tool_progress': {
        const toolUseId = msg.tool_use_id as string
        const toolName = msg.tool_name as string
        const elapsed = msg.elapsed_time_seconds as number
        if (toolUseId && toolName) {
          setActiveTools(prev => {
            const next = new Map(prev)
            next.set(toolUseId, { toolName, elapsed })
            return next
          })
        }
        break
      }

      // ── Status changes (compacting) ──────────────────────────────────
      case 'status_change': {
        const status = msg.status as string | null
        setIsCompacting(status === 'compacting')
        break
      }

      // ── Tool use summaries ───────────────────────────────────────────
      case 'tool_use_summary': {
        const summary = msg.summary as string
        const toolUseIds = (msg.tool_use_ids as string[]) || []
        if (summary) {
          setMessages(prev => [...prev, { type: 'tool_use_summary', summary, tool_use_ids: toolUseIds }])
          // Clear progress for summarized tools
          if (toolUseIds.length > 0) {
            setActiveTools(prev => {
              const next = new Map(prev)
              for (const id of toolUseIds) next.delete(id)
              return next.size !== prev.size ? next : prev
            })
          }
        }
        break
      }

      case 'screenshot_result':
        setMessages(prev => [...prev, msg as unknown as ChatMessage])
        break

      case 'auth_status': {
        const authenticated = msg.authenticated as boolean | undefined
        if (authenticated === false) {
          // Credentials absent (fresh container OR user just signed out).
          // Reset login state regardless of prior status so the login card reappears
          // after a manual logout — the user was previously 'complete'.
          setLoginState({ status: 'idle', error: 'not_authenticated' })
        }
        break
      }

      case 'system_event':
        break

      case 'conversations_list':
        setConversations((msg.conversations as ConversationSummary[]) || [])
        break

      case 'event_replay': {
        // Smart replay: process each replayed event through the normal handler
        const events = (msg.events as { seq: number; message: Record<string, unknown> }[]) || []
        for (const evt of events) {
          if (typeof evt.seq === 'number' && evt.seq > lastSeqRef.current) {
            lastSeqRef.current = evt.seq
          }
          handleMessageRef.current(evt.message)
        }
        break
      }

      case 'session_phase': {
        // Ground-truth phase from the bridge state machine. When it says the
        // session is idle, clear any phantom activity left over from replay or
        // a consumer handoff (stale streaming text, activeTools with no
        // clearing result coming) — otherwise the panel shows a Stop button
        // with nothing actually running.
        const p = (msg.phase as string) || null
        setPhase(p)
        if (p !== 'streaming' && p !== 'awaiting_permission' && p !== 'compacting') {
          clearStreamingState()
          setActiveTools(new Map())
          setIsCompacting(false)
        }
        break
      }

      // ── Login flow ─────────────────────────────────────────────────
      case 'login_url':
        setLoginState({ status: 'awaiting_code', url: msg.url as string })
        break

      case 'login_complete':
        setLoginState({ status: 'complete' })
        break

      case 'login_error':
        setLoginState({ status: 'error', error: msg.message as string })
        break

      case 'error':
        console.warn('[claude-session] Bridge error:', msg.message)
        break
    }
  }

  // Persist messages and session state to sessionStorage for instant reload
  const saveCacheRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (saveCacheRef.current) clearTimeout(saveCacheRef.current)
    saveCacheRef.current = setTimeout(() => {
      saveCache(sessionId, 'msgs', messages.slice(-200)) // cap cached messages
      if (sessionState) saveCache(sessionId, 'state', sessionState)
      saveCache(sessionId, 'questions', pendingQuestions)
    }, 500) // debounce 500ms
    return () => { if (saveCacheRef.current) clearTimeout(saveCacheRef.current) }
  }, [messages, sessionState, pendingQuestions, sessionId])

  // Reset per-session live state when `sessionId` itself changes (e.g. the
  // user switches work areas while the panel stays mounted — the parent
  // renders the SAME ClaudePanel/useClaudeSession instance with a new
  // `session` prop, it isn't remounted). Without this, every piece of live
  // state below still describes the OLD session until fresh frames arrive
  // for the new one: a session that was genuinely mid-turn when the user
  // switched away keeps showing "Claude is working" (stale phase /
  // isStreaming / activeTools) under the new session's identity. Guarded so
  // it never fires on the initial mount, where the useState initializers
  // above already hydrated from this exact sessionId's cache.
  const prevSessionIdRef = useRef(sessionId)
  useEffect(() => {
    if (prevSessionIdRef.current === sessionId) return
    prevSessionIdRef.current = sessionId
    setMessages(loadCached(sessionId, 'msgs') || [])
    setSessionState(loadCached(sessionId, 'state'))
    setPhase(null)
    setCliConnected(false)
    // The socket for the OLD session is being torn down by the connect effect;
    // without this the composer stays enabled against a dead socket and
    // sendMessage silently drops input until the new session's onopen.
    setConnected(false)
    resetTransientSessionState()
    // Override the [] pendingQuestions reset above with this session's cache —
    // both setters land in the same batch, so the cached value wins.
    setPendingQuestions(loadCached(sessionId, 'questions') || [])
  }, [sessionId, resetTransientSessionState])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    mountedRef.current = true
    connect()

    // Reconnect immediately when tab becomes visible (e.g., after page reload
    // or switching back from another tab). Resets backoff for fast recovery.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && mountedRef.current) {
        const ws = wsRef.current
        if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
          reconnectDelayRef.current = 1000 // reset backoff
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = null
          }
          connect()
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      mountedRef.current = false
      document.removeEventListener('visibilitychange', handleVisibility)
      if (streamRafRef.current) {
        cancelAnimationFrame(streamRafRef.current)
        streamRafRef.current = 0
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (wsRef.current) {
        wsRef.current.onclose = null   // Prevent stale reconnect trigger
        wsRef.current.onmessage = null // Prevent stale messages after remount
        wsRef.current.close()
        wsRef.current = null
      }
    }
  }, [connect])

  const sendMessage = useCallback((text: string, images?: ImageAttachment[]) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    const msg: Record<string, unknown> = { type: 'user_message', content: text }
    if (images && images.length > 0) msg.images = images
    ws.send(JSON.stringify(msg))
  }, [])

  const respondToPermission = useCallback((requestId: string, behavior: 'allow' | 'deny') => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'permission_response', request_id: requestId, behavior }))
    setPendingPermissions(prev => prev.filter(p => p.request_id !== requestId))
  }, [])

  const respondToQuestion = useCallback((requestId: string, answers: Record<string, string>, behavior?: 'allow' | 'deny') => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'question_response', request_id: requestId, answers, behavior: behavior || 'allow' }))
    setPendingQuestions(prev => prev.filter(q => q.request_id !== requestId))
  }, [])

  const interrupt = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'interrupt' }))
  }, [])

  // The agent kind the panel wants driving this session. Held in a ref because
  // the ws.onopen closure that sends session_subscribe is built once per
  // connection and would otherwise capture a stale value.
  const agentRef = useRef(agent)
  useEffect(() => { agentRef.current = agent }, [agent])

  /**
   * Ask the bridge to switch agents. Re-subscribing is the switch: the server
   * tears down the outgoing agent's CLI and replies with a fresh session_init
   * plus an empty history, since the two CLIs cannot resume each other's
   * conversation. last_seq 0 forces that full replay.
   */
  const setAgent = useCallback((kind: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    agentRef.current = kind
    ws.send(JSON.stringify({ type: 'session_subscribe', last_seq: 0, agent: kind }))
  }, [])

  const setModel = useCallback((model: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'set_model', model }))
  }, [])

  const setPermissionMode = useCallback((mode: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'set_permission_mode', mode }))
  }, [])

  const startLoginFn = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    setLoginState({ status: 'awaiting_url' })
    ws.send(JSON.stringify({ type: 'start_login' }))
  }, [])

  const submitLoginCode = useCallback((code: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    setLoginState({ status: 'submitting' })
    ws.send(JSON.stringify({ type: 'submit_login_code', code }))
  }, [])

  const logout = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'logout' }))
    // Clear transient UI state immediately — the bridge's auth_status
    // broadcast will flip the login card on, but clearing here avoids a
    // flicker where stale messages look interactive.
    setPendingPermissions([])
    setPendingQuestions([])
    setLoginState({ status: 'idle', error: 'not_authenticated' })
  }, [])

  const listConversationsFn = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'list_conversations' }))
  }, [])

  const resumeConversation = useCallback((id: string) => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'resume_conversation', id }))
    // The bridge rebuilds history and resets its event sequence — mirror
    // newSession: clear transient state and drop last_seq so the next
    // subscribe requests full history instead of a bogus partial replay.
    resetTransientSessionState()
  }, [resetTransientSessionState])

  const newSession = useCallback(() => {
    const ws = wsRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    ws.send(JSON.stringify({ type: 'new_session' }))
    // Optimistic UI reset — the bridge's message_history:[] broadcast will
    // follow and confirm. Clearing here avoids a visible flicker.
    setMessages([])
    resetTransientSessionState()
  }, [resetTransientSessionState])

  return {
    connected,
    cliConnected,
    messages,
    sessionState,
    pendingPermissions,
    pendingQuestions,
    streamingText,
    streamingThinking,
    isStreaming,
    isCompacting,
    activeTools,
    loginState,
    phase,
    conversations,
    listConversations: listConversationsFn,
    resumeConversation,
    sendMessage,
    respondToPermission,
    respondToQuestion,
    interrupt,
    setModel,
    setAgent,
    setPermissionMode,
    startLogin: startLoginFn,
    submitLoginCode,
    logout,
    newSession,
  }
}
